import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { SkillSummary } from "@/api";
import { deleteSkillMutation, listSkillsQueryKey, skillDeleteImpactOptions } from "@/api";
import { useDeleteEntity } from "@/components/build/useDeleteEntity";
import { Text } from "@/components/Text";

/** Delete-with-confirm for a skill, with the delete-impact (sibling imported agents + attached-agent count). */
export function useDeleteSkill(onDeleted?: () => void) {
  const { t } = useTranslation("skills");
  return useDeleteEntity<SkillSummary>({
    mutation: deleteSkillMutation(),
    listQueryKey: listSkillsQueryKey,
    pathFor: (s) => ({ skill_uuid: s.uuid }),
    messages: { deleted: t("deleted"), deleteFailed: t("deleteFailed") },
    onDeleted,
    renderExtra: (skill) => <SkillDeleteImpact uuid={skill.uuid} />,
  });
}

/** The delete-impact body for the confirm dialog (its own query). */
function SkillDeleteImpact({ uuid }: Readonly<{ uuid: string }>) {
  const { t } = useTranslation("skills");
  const impact = useQuery(skillDeleteImpactOptions({ path: { skill_uuid: uuid } }));
  const siblings = impact.data?.data.siblingAgents ?? [];
  const attachedCount = impact.data?.data.attachedAgentCount ?? 0;

  if (impact.isPending) {
    return (
      <Text variant="caption" tone="tertiary">
        {t("impact.checking")}
      </Text>
    );
  }
  return (
    <>
      {attachedCount > 0 && (
        <Text variant="body-sm" tone="warning">
          {t("impact.attached", { count: attachedCount })}
        </Text>
      )}
      {siblings.length > 0 && (
        <Text variant="body-sm" tone="warning">
          {t("impact.siblings", { names: siblings.map((s) => s.name).join(", ") })}
        </Text>
      )}
    </>
  );
}
